import React, { useState } from 'react';
import { AuthenticatedTemplate } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { ExpandLess, ExpandMore, Add as CreateIcon, Search as LookupIcon, Dashboard as DashboardIcon } from '@mui/icons-material';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText, Chip, Box } from '@mui/material';
import { useAppSelector } from '../../../../../app/hooks';
import { useGetMyProfileQuery } from '../../../../../services/ProfileService';
import { hasWriteoffsAccess } from '../../../../profile/utils/profileUtils';

// Types
interface WriteoffMenuItemConfig {
    id: string;
    title: string;
    icon: React.ComponentType<{ sx?: any }>;
    route: string;
    showCount?: boolean;
}

// Constants
const MENU_STYLES = {
    navList: { 
        border: 'black solid 1px' 
    },
    listItem: { 
        marginTop: '0px' 
    },
    nestedListItem: { 
        marginTop: '0px',
        paddingLeft: '48px'
    },
    icon: { 
        color: 'white' 
    },
    countContainer: { 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        width: '100%' 
    },
    countChip: {
        backgroundColor: '#ef4444',
        color: 'white',
        fontSize: '0.75rem',
        minWidth: '24px',
        height: '20px'
    }
} as const;

const WRITEOFF_MENU_ITEMS: WriteoffMenuItemConfig[] = [
    {
        id: 'dashboard',
        title: 'Dashboard',
        icon: DashboardIcon,
        route: '/writeoffs/dashboard',
        showCount: false
    },
    {
        id: 'create',
        title: 'Candidates',
        icon: CreateIcon,
        route: '/writeoffs/create',
        showCount: false
    },
    {
        id: 'lookup',
        title: 'Lookup',
        icon: LookupIcon,
        route: '/writeoffs/lookup',
        showCount: false
    }
];

// Sub-components
interface WriteoffMenuItemProps {
    item: WriteoffMenuItemConfig;
    onClick: () => void;
    count?: number;
    disabled?: boolean;
}

const WriteoffMenuItem: React.FC<WriteoffMenuItemProps> = ({ item, onClick, count, disabled = false }) => {
    const { title, icon: Icon, showCount } = item;
    const shouldShowCount = showCount && count && count > 0;

    return (
        <ListItemButton 
            sx={{
                ...MENU_STYLES.nestedListItem,
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer'
            }} 
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
        >
            <ListItemIcon>
                <Icon sx={MENU_STYLES.icon} />
            </ListItemIcon>
            {shouldShowCount ? (
                <Box sx={MENU_STYLES.countContainer}>
                    <ListItemText primary={title} />
                    <Chip 
                        label={count} 
                        size="small" 
                        sx={MENU_STYLES.countChip}
                    />
                </Box>
            ) : (
                <ListItemText primary={title} />
            )}
        </ListItemButton>
    );
};

// Main component
export const WriteoffMenu: React.FC = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    
    const selectedCenter = useAppSelector(state => state.center.selectedCenter);
    const { data: profile } = useGetMyProfileQuery();
    const company = profile?.organization || '';

    const handleItemClick = (item: WriteoffMenuItemConfig): void => {
        if (item.route === '/writeoffs/lookup' || item.route === '/writeoffs/dashboard') {
            if (!company || !selectedCenter) {
                console.warn('Cannot navigate to writeoff page: missing required parameters', {
                    company: company || 'missing',
                    selectedCenter: selectedCenter || 'missing'
                });
                return;
            }
        }

        const params = new URLSearchParams();
        if (company) params.append('company', company);
        if (selectedCenter) params.append('center', selectedCenter);
        
        const routeWithParams = `${item.route}?${params.toString()}`;
        navigate(routeWithParams);
    };

    const toggleMenu = (): void => setOpen(!open);

    if (!hasWriteoffsAccess(profile)) {
        return null;
    }

    return (
        <AuthenticatedTemplate>
            <List component="nav" sx={MENU_STYLES.navList}>
                <ListItemButton onClick={toggleMenu}>
                    <ListItemText primary="Writeoffs" />
                    {open ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={open} timeout="auto" unmountOnExit>
                    {WRITEOFF_MENU_ITEMS.map((item) => {
                        const isDisabled = (item.route === '/writeoffs/lookup' || item.route === '/writeoffs/dashboard') && !company;
                        
                        return (
                            <WriteoffMenuItem
                                key={item.id}
                                item={item}
                                onClick={() => handleItemClick(item)}
                                disabled={isDisabled}
                            />
                        );
                    })}
                </Collapse>
            </List>
        </AuthenticatedTemplate>
    );
};

export default WriteoffMenu;
